import { useCallback, useState, useMemo, useEffect } from 'react';
import { kebabCase } from 'lodash';
import type {
  Brand,
  Category,
  Collection,
  DataLoaders,
  Dispensary,
  LoyaltyData,
  Product as RemoteProduct,
  Special,
  ProductImage as RemoteProductImage,
  ProductPotency as RemoteProductPotency,
} from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRouter } from 'next/compat/router';
import { useApolloClient, useQuery } from '@apollo/client';

import {
  GetCollectionsDocument,
  GetRemoteIntegrationValueDocument,
  IndividualFilteredProductDocument,
} from 'src/gql/graphql';
import { Product } from 'types/graphql-overrides';
import type {
  Brand as BrandGql,
  Collection as CollectionGql,
  IndividualFilteredProductQuery,
  Specials,
} from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import type { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import { useDispensaryChains } from 'src/dispensary/hooks/use-dispensary-chain';
import { WhenNotVoid } from 'shared/utils/type-utils';
import { buildCategoryRoute } from 'shared/helpers/route-helpers';
import { getVariables } from 'src/hooks/use-product';
import useUI from 'src/hooks/use-ui';
import { useLoyalty, UseLoyaltyReturn } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

type ResolvablePromise<T> = Promise<T> & {
  resolve?: (value: T) => void;
};

function useCreateLazyDataLoader<T>(
  useData: (skip: boolean) => { data: T; loading: boolean },
  dependencies: any[] = []
): { data: T; dataLoader: () => Promise<T> } {
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  // create once
  const resolvablePromise = useMemo<ResolvablePromise<T>>(() => {
    let outerResolve: ((value: T) => void) | null = null;
    const promise: ResolvablePromise<T> = new Promise<T>((resolve) => {
      outerResolve = resolve;
    });
    // we know this is assigned because the callback in promises constructor is called syncronously
    promise.resolve = (outerResolve as unknown) as (value: T) => void;

    return promise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  const { data, loading } = useData(!shouldFetch);

  useEffect(() => {
    if (resolvablePromise.resolve && shouldFetch && !loading) {
      const { resolve } = resolvablePromise;
      delete resolvablePromise.resolve;
      resolve(data);
    }
  }, [data, loading, shouldFetch, resolvablePromise]);

  const dataLoader = useCallback(async () => {
    setShouldFetch(true);

    return resolvablePromise;
  }, [resolvablePromise]);

  return { data, dataLoader };
}

export const useBridgeDataLoaders = (): DataLoaders => {
  const apolloClient = useApolloClient();
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { categories } = useDispensaryCategories();
  const { brands } = useMenuFilterOptions({ skipCurrentFilters: true });
  const router = useRouter();
  const { query } = router ?? {};
  const { isDemo, isKiosk, isEmbedded, isStoreFront, isMobileEcommApp } = UI;

  // Expensive Data Loaders - Use lazy loading to only load when requested.

  // SPECIALS
  const { dataLoader: getAllSpecials } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { specials, loading } = useDispensarySpecials({
      skip,
    });

    return { data: specials, loading: skip || loading };
  });

  // PRODUCTS
  const { dataLoader: getAllProducts } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { products, loading } = useDispensaryProducts({
      skip,
    });

    return { data: products, loading: skip || loading };
  });

  const { dataLoader: getProduct } = useCreateLazyDataLoader(
    (skip) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: individualFilteredProductData, loading } = useQuery(IndividualFilteredProductDocument, {
        variables: getVariables(query?.productId, dispensary, UI.isKiosk),
        skip: skip || !query?.productId,
      });

      return { data: individualFilteredProductData?.filteredProducts?.products[0], loading: skip || loading };
    },
    [query?.productId]
  );

  // LOCATIONS
  const { dataLoader: getAllLocations } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { dispensaryChainLocations, loading } = useDispensaryChains({
      activeOnly: true,
      includeStealthMode: true,
      skip,
    });

    return { data: dispensaryChainLocations ?? [], loading: skip || loading };
  });

  // COLLECTIONS
  const { dataLoader: getAllCollections } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: collectionsData, loading } = useQuery(GetCollectionsDocument, {
      skip: skip || !dispensary?.id,
      variables: {
        dispensaryId: dispensary?.id ?? '',
        customCategory: false,
        activeOnly: true,
      },
    });

    return { data: collectionsData?.getCollections ?? [], loading: skip || loading };
  });

  // INTEGRATION VALUE
  const getIntegrationValue = useCallback(
    async (key = '') => {
      const result = await apolloClient.query({
        query: GetRemoteIntegrationValueDocument,
        variables: {
          dispensaryId: dispensary?.id ?? '',
          key,
        },
      });
      return result.data;
    },
    [apolloClient, dispensary?.id]
  );

  // LOYALTY
  const { dataLoader: getLoyalty } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { loading, ...data } = useLoyalty(undefined, undefined, undefined, { skip });

    return { data, loading: skip || loading };
  }, []);

  const mapCategoryToRemote = createMapCategoryToRemote({
    isDemo,
    isKiosk,
    isEmbedded,
    isStoreFront,
    isMobileEcommApp,
    dispensary,
  });

  return {
    brands: async () => {
      const result = await Promise.resolve(brands);
      return result.map(mapBrandToRemote);
    },
    categories: async () => {
      const result = await Promise.resolve(categories);
      return result.map(mapCategoryToRemote);
    },
    collections: async () => {
      const result = await getAllCollections();
      return result.map(mapCollectionToRemote);
    },
    integrationValue: async (key: string) => {
      const result = await getIntegrationValue(key);
      return result?.getRemoteIntegrationValue ?? undefined;
    },
    locations: async () => {
      const result = await getAllLocations();
      return result.map(mapDispensaryToRemote).filter((d): d is Dispensary => d !== undefined);
    },
    product: async () => {
      const result = await getProduct();
      return result ? mapProductToRemote(result) : null;
    },
    products: async () => {
      const result = await getAllProducts();
      return result.map(mapProductToRemote);
    },
    specials: async () => {
      const result = await getAllSpecials();
      return result.map(mapSpecialToRemote);
    },
    loyalty: async () => {
      const result = await getLoyalty();
      // Per the SDK contract, loyalty() returns null when the user isn't logged in or loyalty
      // isn't configured/applicable for this dispensary -- loyaltyWidgetEnabled already
      // encapsulates both of those conditions (see useLoyalty's shouldShowLoyaltyWidget).
      return result.loyaltyWidgetEnabled ? mapLoyaltyToRemote(result) : null;
    },
  };
};

const mapBrandToRemote = (brand: BrandGql): Brand => ({
  cname: kebabCase(brand.name),
  description: brand.description ?? undefined,
  id: brand.id,
  image: brand.imageUrl ?? '',
  name: brand.name,
});

function createMapCategoryToRemote({ isDemo, isKiosk, isEmbedded, isStoreFront, isMobileEcommApp, dispensary }) {
  return (category: DispensaryMenuCategory): Category & { href: string; route: string } => {
    const [href, route] = buildCategoryRoute({
      isDemo,
      isKiosk,
      isEmbedded,
      isStoreFront,
      isMobileEcommApp,
      dispensary,
      category: category.key,
    });

    return {
      cname: category.slug ?? category.key,
      id: category.key,
      isOutlet: category.isOutlet,
      name: category.label,
      href,
      route,
    };
  };
}

const mapCollectionToRemote = (collection: CollectionGql): Collection => ({
  cname: collection.slug,
  id: collection.id,
  name: collection.title,
  position: collection.position ?? undefined,
  cardBadgeColor: collection.cardBadgeColor ?? undefined,
  cardBadgeTitle: collection.cardBadgeTitle ?? undefined,
  cardImage: collection.cardImage ?? undefined,
  description: collection.description ?? undefined,
  image: collection.image ?? undefined,
  isExclusive: collection.isExclusive ?? undefined,
  isOutlet: collection.isOutlet ?? undefined,
  navigationEnabled: collection.navigationEnabled,
  navigationTitle: collection.navigationTitle ?? undefined,
  outletTimerMessage: collection.outletTimerMessage ?? undefined,
  productCount: collection.productCount ?? undefined,
});

type ProductTag = { id: string; name: string };
type MappedProduct = RemoteProduct & { tags?: ProductTag[] };

type MappableProduct =
  | Product
  | WhenNotVoid<WhenNotVoid<IndividualFilteredProductQuery['filteredProducts']>['products'][number]>;

const filterNulls = <T>(values?: (T | null)[] | null): T[] | undefined =>
  values?.filter((value): value is T => value != null);

const mapPotencyToRemote = (
  potency?: { range?: (number | null)[] | null; unit: string } | null
): RemoteProductPotency | undefined =>
  potency ? { range: filterNulls(potency.range), unit: potency.unit } : undefined;

const mapImagesToRemote = (
  images?: { url: string; description?: string | null; active: boolean }[] | null
): RemoteProductImage[] | undefined =>
  images
    ?.filter((image) => image.active)
    .map((image) => ({ url: image.url, description: image.description ?? undefined }));

const mapTagsToRemote = (
  tags?: ({ tagId?: string | null; tagName?: string | null } | null)[] | null
): ProductTag[] | undefined => {
  const mapped = tags?.flatMap((tag) => (tag?.tagId && tag?.tagName ? [{ id: tag.tagId, name: tag.tagName }] : []));
  return mapped?.length ? mapped : undefined;
};

const mapProductToRemote = (product: MappableProduct): MappedProduct => {
  const prices = filterNulls((product as Product).prices ?? product.Prices);

  return {
    brandId: product.brandId ?? undefined,
    brandName: product.brandName ?? undefined,
    category: product.type ?? undefined,
    cbdContent: mapPotencyToRemote(product.CBDContent),
    cname: product.cName ?? product.id ?? '',
    comingSoon: product.comingSoon ?? undefined,
    description: product.description ?? '',
    effects: product.effects ?? undefined,
    featured: product.featured?.current ?? undefined,
    id: product.id ?? '',
    image: product.Image ?? '',
    images: mapImagesToRemote(product.images),
    medicalOnly: product.medicalOnly ?? undefined,
    medicalPrices: filterNulls(product.medicalPrices),
    medicalSpecialPrices: filterNulls(product.medicalSpecialPrices),
    name: product.Name ?? '',
    options: filterNulls(product.Options),
    price: prices?.[0] ?? 0,
    prices,
    recOnly: product.recOnly ?? undefined,
    recPrices: filterNulls(product.recPrices),
    recSpecialPrices: filterNulls(product.recSpecialPrices),
    strainType: product.strainType ?? undefined,
    subcategory: product.subcategory ?? undefined,
    tags: mapTagsToRemote(product.POSMetaData?.canonicalProductTags),
    terpenes: (product.terpenes ?? [])
      .filter((t) => !/total\s+terpenes/i.test(t?.libraryTerpene?.name ?? ''))
      .map((t) => ({
        name: t?.libraryTerpene?.name ?? '',
        unit: t?.unitSymbol ?? t?.unit ?? undefined,
        value: t?.value ?? undefined,
      })),
    thcContent: mapPotencyToRemote(product.THCContent),
    weight: product.weight ?? undefined,
  };
};

const resolveSpecialImage = (menuDisplayImage?: string | null): string => {
  const normalized = replaceAWSSourceWithImgix(menuDisplayImage);
  return normalized.includes('://') ? normalized : '';
};

const resolveSpecialDate = (stamp: Date | string | null | undefined): string | undefined => {
  if (!stamp) {
    return undefined;
  }
  const date = stamp instanceof Date ? stamp : new Date(stamp);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const mapSpecialToRemote = (special: Specials): Special => ({
  cname: kebabCase(special.name ?? ''),
  description: special.menuDisplayDescription ?? '',
  descriptionHtml: special.descriptionHtml ?? undefined,
  displayRank: special.displayRank ?? undefined,
  endDate: resolveSpecialDate(special.endStamp),
  heading: special.heading ?? undefined,
  id: special._id ?? '',
  image: resolveSpecialImage(special.menuDisplayImage),
  isRecurring: special.isRecurring ?? undefined,
  menuType: special.menuType ?? undefined,
  name: special.name ?? '',
  startDate: resolveSpecialDate(special.startStamp),
  type: special.specialType ?? undefined,
});

const mapLoyaltyToRemote = (loyaltyData: Omit<UseLoyaltyReturn, 'loading'>): LoyaltyData => ({
  balance: loyaltyData.currentBalance.toString(),
  currentTierName: loyaltyData.currentTierName ?? '',
  currentTierThreshold: loyaltyData.currentTierThreshold ?? 0,
  nextTierName: loyaltyData.nextTierName ?? '',
  nextTierThreshold: loyaltyData.targetBalance,
  expirationDate: loyaltyData.expirationDate ?? null,
  rewardsProgramName: loyaltyData.rewardsProgramName,
  balanceDaysToExpiration: loyaltyData.balanceDaysToExpiration ?? null,
  badgeColor: loyaltyData.badgeColor,
  progressPercentage: loyaltyData.progressPercentageToNextTier,
  isLoyaltyMember: loyaltyData.isLoyaltyMember,
  isPoints: loyaltyData.isPoints,
  isDiscountRedemption: loyaltyData.isDiscountRedemption,
});
